import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DailyIframe from '@daily-co/daily-js';
import { Box, Typography, Button, CircularProgress, Alert, Chip } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import dailyCoService from '../services/dailyCoService';
import CallControls from './video/CallControls';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';

// Module-level singleton to prevent duplicate DailyIframe instances
// Daily.co only allows one instance globally, so we need to track it at module level
let globalCallFrameInstance = null;
let globalInitializationLock = false;
let globalInitializationPromise = null; // Track ongoing initialization

const VideoCall = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const callFrameRef = useRef(null);
  const containerRef = useRef(null);
  const isInitializingRef = useRef(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('connecting'); // 'connecting', 'connected', 'disconnected', 'error'
  const [callType, setCallType] = useState('video'); // 'video' or 'audio'
  const connectionTimeoutRef = useRef(null);

  // Prevent body scroll when video call is active
  useEffect(() => {
    // Lock body scroll when component mounts
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore body scroll when component unmounts
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    // Prevent duplicate initialization (React StrictMode runs effects twice)
    // Check both component-level and module-level guards
    if (isInitializingRef.current || callFrameRef.current || globalInitializationLock || globalCallFrameInstance) {
      if (import.meta.env.DEV) console.log('Skipping duplicate initialization - instance already exists');
      // If global instance exists, reuse it
      if (globalCallFrameInstance && !callFrameRef.current) {
        if (import.meta.env.DEV) console.log('Reusing existing global instance');
        callFrameRef.current = globalCallFrameInstance;
        // Re-attach event listeners
        attachEventListeners(globalCallFrameInstance);
        // Try to join if not already joined
        const urlParams = new URLSearchParams(window.location.search);
        let appointmentId = urlParams.get('appointmentId');
        // Backward compatibility: also check for roomId
        if (!appointmentId) {
          appointmentId = urlParams.get('roomId');
        }
        if (!appointmentId) {
          console.error('[VideoCall] Cannot join - missing appointmentId in URL');
          return;
        }
        dailyCoService.createVideoCallRoom(
          appointmentId,
          `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
        ).then(response => {
          if (response?.roomUrl && globalCallFrameInstance) {
            globalCallFrameInstance.join({ url: response.roomUrl, token: response.token });
          }
        }).catch(err => console.error('Error joining with existing instance:', err));
      }
      return;
    }

    // If there's an ongoing initialization, wait for it
    if (globalInitializationPromise) {
      if (import.meta.env.DEV) console.log('Waiting for ongoing initialization...');
      globalInitializationPromise.then(() => {
        if (globalCallFrameInstance) {
          callFrameRef.current = globalCallFrameInstance;
          attachEventListeners(globalCallFrameInstance);
        }
      }).catch(() => {
        // If it failed, try again
        initializeCall();
      });
      return;
    }

    initializeCall();
    
    return () => {
      // In React StrictMode, cleanup runs synchronously before the second effect
      // If initialization is in progress, don't destroy - let the second effect reuse it
      if (globalInitializationLock || globalInitializationPromise) {
        if (import.meta.env.DEV) console.log('Skipping cleanup - initialization in progress (likely StrictMode)');
        return;
      }
      
      // Only cleanup if this is a real unmount (not StrictMode)
      // Check if we're the owner of the global instance
      if (callFrameRef.current && globalCallFrameInstance === callFrameRef.current) {
        try {
          if (import.meta.env.DEV) console.log('Cleaning up DailyIframe instance (real unmount)');
          callFrameRef.current.destroy();
          globalCallFrameInstance = null;
        } catch (err) {
          console.warn('Error destroying DailyIframe:', err);
        }
      }
      
      // Clear connection timeout
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      
      callFrameRef.current = null;
      isInitializingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run once on mount. Functions are stable.

  const attachEventListeners = (callFrame) => {
    // Note: Daily.co's off() requires the listener function, so we can't easily remove
    // existing listeners. Instead, we'll just attach new ones - Daily.co handles
    // duplicate listeners internally by replacing them for the same event type.
    
    // Event listeners
    callFrame.on('joined-meeting', () => {
      if (import.meta.env.DEV) console.log('[VideoCall] joined-meeting event fired');
      setLoading(false);
      setConnectionStatus('connected');
      // Clear any connection timeout
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      // Get initial audio/video state
      try {
        setIsMuted(!callFrame.localAudio());
        setIsVideoEnabled(callFrame.localVideo());
      } catch (error) {
        console.warn('Error getting initial audio/video state:', error);
      }
    });

    // Also listen for 'participant-joined' to detect when we're actually in the call
    callFrame.on('participant-joined', (event) => {
      if (import.meta.env.DEV) console.log('[VideoCall] participant-joined event fired', event);
      if (loading) {
        setLoading(false);
        setConnectionStatus('connected');
      }
    });

    callFrame.on('left-meeting', () => {
      setConnectionStatus('disconnected');
      navigate('/dashboard');
    });

    callFrame.on('error', (error) => {
      console.error('Daily.co error:', error);
      setError('Video call error occurred');
      setConnectionStatus('error');
    });

    // Track connection state changes
    callFrame.on('participant-joined', () => {
      setConnectionStatus('connected');
    });

    callFrame.on('participant-left', () => {
      // Don't change status if we're still in the call
      try {
        const participants = callFrame.participants();
        if (participants && Object.keys(participants).length > 0) {
          setConnectionStatus('connected');
        }
      } catch (error) {
        // Ignore errors checking participants
      }
    });

    // Track audio/video state changes
    callFrame.on('local-audio-started', () => {
      setIsMuted(false);
    });

    callFrame.on('local-audio-stopped', () => {
      setIsMuted(true);
    });

    callFrame.on('local-video-started', () => {
      setIsVideoEnabled(true);
    });

    callFrame.on('local-video-stopped', () => {
      setIsVideoEnabled(false);
    });
  };

  const initializeCall = async () => {
    // Prevent duplicate calls - check both component and global state
    if (isInitializingRef.current || callFrameRef.current || globalInitializationLock || globalCallFrameInstance) {
      if (import.meta.env.DEV) console.log('Skipping duplicate call initialization');
      return;
    }

    isInitializingRef.current = true;
    globalInitializationLock = true;
    
    // Create a promise to track this initialization
    globalInitializationPromise = (async () => {

    try {
      // Get appointment ID from URL - check both appointmentId and roomId for backward compatibility
      // If missing, this is an error - both parties must use the same room identifier
      const urlParams = new URLSearchParams(window.location.search);
      let appointmentId = urlParams.get('appointmentId');
      
      // Backward compatibility: also check for roomId (old format)
      if (!appointmentId) {
        appointmentId = urlParams.get('roomId');
        if (appointmentId) {
          console.warn('[VideoCall] Using deprecated roomId parameter - should use appointmentId');
        }
      }
      
      // Get call type from URL (defaults to 'video')
      const typeParam = urlParams.get('type');
      setCallType(typeParam === 'audio' ? 'audio' : 'video');

      // Check if this is a request to start a new call (has userType but no appointmentId)
      const userType = urlParams.get('userType');
      if (!appointmentId && userType) {
        // User wants to start a new call - redirect to dashboard
        // The dashboard will handle opening the StartVideoCallModal
        if (import.meta.env.DEV) console.log('[VideoCall] No appointmentId but userType present - redirecting to dashboard');
        setLoading(false);
        navigate('/dashboard');
        return; // Exit early, don't try to join a room
      }
      
      if (!appointmentId) {
        const errorMsg = 'Missing appointmentId in URL. Cannot join video call without room identifier. Please return to the dashboard and start a new call.';
        console.error('[VideoCall]', errorMsg);
        console.error('[VideoCall] Current URL:', window.location.href);
        console.error('[VideoCall] URL params:', Object.fromEntries(urlParams));
        setError(errorMsg);
        setLoading(false);
        isInitializingRef.current = false;
        globalInitializationLock = false;
        
        // Auto-redirect to dashboard after 5 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 5000);
        return; // Don't throw, just show error
      }
      
      // CRITICAL: Log the appointmentId being used for debugging
      if (import.meta.env.DEV) console.log('[VideoCall] Initializing call with appointmentId:', appointmentId);
      if (import.meta.env.DEV) console.log('[VideoCall] Full URL params:', window.location.search);
      
      // Create room and get token
      const response = await dailyCoService.createVideoCallRoom(
        appointmentId,
        `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
      );
      
      if (import.meta.env.DEV) console.log('[VideoCall] Room response:', {
        roomName: response?.roomName,
        roomUrl: response?.roomUrl,
        hasToken: !!response?.token
      });
      
      // Extract room URL and token from response
      const roomUrl = response?.roomUrl;
      const token = response?.token;
      
      if (!roomUrl) {
        throw new Error('Failed to get room URL from server');
      }

      // Clean up any existing global instance before creating a new one
      if (globalCallFrameInstance) {
        try {
          if (import.meta.env.DEV) console.log('Destroying existing global DailyIframe instance');
          globalCallFrameInstance.destroy();
        } catch (err) {
          console.warn('Error destroying existing global DailyIframe:', err);
        }
        globalCallFrameInstance = null;
      }

      // Clean up component-level instance
      if (callFrameRef.current) {
        try {
          callFrameRef.current.destroy();
        } catch (err) {
          console.warn('Error destroying existing DailyIframe:', err);
        }
        callFrameRef.current = null;
      }

      // Wait for container to be ready
      if (!containerRef.current) {
        throw new Error('Container element not available');
      }

      // Initialize Daily.co iframe - only one instance allowed globally
      // Wrap in try-catch to handle race conditions
      let callFrame;
      try {
        if (import.meta.env.DEV) console.log('Creating new DailyIframe instance');
        callFrame = DailyIframe.createFrame(containerRef.current, {
          iframeStyle: {
            width: '100%',
            height: '100%',
            border: '0',
            borderRadius: 0, // Remove border radius for full-screen fit
            display: 'block',
          },
          showLeaveButton: true,
          showFullscreenButton: true,
          // Improve mobile experience
          customLayout: true,
        });
      } catch (err) {
        // If we get duplicate error, wait a bit and check if global instance exists
        if (err.message?.includes('Duplicate') || err.message?.includes('not allowed')) {
          console.warn('Duplicate instance detected, waiting for existing instance...');
          // Wait longer to let the first instance fully initialize
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // If global instance exists, use it - DO NOT try to create a new one
          if (globalCallFrameInstance) {
            if (import.meta.env.DEV) console.log('Reusing existing global DailyIframe instance');
            callFrame = globalCallFrameInstance;
          } else {
            // If no global instance after waiting, the first one must have failed
            // Don't retry - just throw the error
            console.error('No global instance found after duplicate error - first instance may have failed');
            throw new Error('Failed to create or reuse DailyIframe instance. Please refresh the page.');
          }
        } else {
          throw err;
        }
      }

      callFrameRef.current = callFrame;
      globalCallFrameInstance = callFrame;

      // Attach event listeners
      attachEventListeners(callFrame);

      // Join the room
      await callFrame.join({ url: roomUrl, token });
      
      if (import.meta.env.DEV) console.log('[VideoCall] Call frame joined, waiting for joined-meeting event...');
      
      // Set a timeout to handle cases where joined-meeting doesn't fire
      // This can happen if Daily.co's UI is still loading
      connectionTimeoutRef.current = setTimeout(() => {
        console.warn('[VideoCall] Connection timeout - joined-meeting event not fired after 10 seconds');
        // Check if we're actually in the call by checking participants
        try {
          const participants = callFrame.participants();
          if (participants && Object.keys(participants).length > 0) {
            if (import.meta.env.DEV) console.log('[VideoCall] Found participants, assuming connected');
            setLoading(false);
            setConnectionStatus('connected');
          } else {
            // Still connecting, but don't show error yet - Daily.co might still be loading
            if (import.meta.env.DEV) console.log('[VideoCall] No participants yet, but call frame exists - may still be connecting');
          }
        } catch (error) {
          console.warn('[VideoCall] Error checking participants:', error);
        }
      }, 10000); // 10 second timeout
      
      // Clear the promise once initialization is complete
      globalInitializationPromise = null;

    } catch (error) {
      console.error('Error initializing call:', error);
      setError(error.message || 'Failed to start video call');
      setLoading(false);
      isInitializingRef.current = false;
      globalInitializationLock = false;
      globalInitializationPromise = null;
      
      // Clean up on error
      if (callFrameRef.current) {
        try {
          callFrameRef.current.destroy();
        } catch (err) {
          console.warn('Error destroying DailyIframe on error:', err);
        }
        callFrameRef.current = null;
      }
      
      // Clear global instance on error
      if (globalCallFrameInstance === callFrameRef.current) {
        globalCallFrameInstance = null;
      }
      
      // Clear connection timeout on error
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    }
    })();
    
    // Wait for initialization to complete
    await globalInitializationPromise;
  };

  // Handle mute toggle
  const handleToggleMute = () => {
    if (!callFrameRef.current) return;
    
    try {
      const currentMuted = callFrameRef.current.localAudio();
      callFrameRef.current.setLocalAudio(!currentMuted);
      setIsMuted(!currentMuted);
    } catch (error) {
      console.error('Error toggling mute:', error);
    }
  };

  // Handle video toggle
  const handleToggleVideo = () => {
    if (!callFrameRef.current) return;
    
    try {
      const currentVideoEnabled = callFrameRef.current.localVideo();
      callFrameRef.current.setLocalVideo(!currentVideoEnabled);
      setIsVideoEnabled(!currentVideoEnabled);
    } catch (error) {
      console.error('Error toggling video:', error);
    }
  };

  // Handle hang up
  const handleHangUp = () => {
    // Clear connection timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    
    if (callFrameRef.current) {
      try {
        callFrameRef.current.leave();
      } catch (error) {
        console.error('Error leaving call:', error);
      }
    }
    
    // If still loading/connecting, navigate immediately
    if (loading) {
      navigate('/dashboard');
    }
    // Otherwise navigation will happen via 'left-meeting' event
  };

  // Handle cancel during connection
  const handleCancelCall = () => {
    if (import.meta.env.DEV) console.log('[VideoCall] Cancel call clicked');
    handleHangUp();
  };

  // Get connection status display
  const getConnectionStatusDisplay = () => {
    switch (connectionStatus) {
      case 'connected':
        return {
          label: 'Connected',
          color: 'success',
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />
        };
      case 'connecting':
        return {
          label: 'Connecting...',
          color: 'warning',
          icon: <WarningIcon sx={{ fontSize: 16 }} />
        };
      case 'disconnected':
        return {
          label: 'Disconnected',
          color: 'error',
          icon: <ErrorIcon sx={{ fontSize: 16 }} />
        };
      case 'error':
        return {
          label: 'Connection Error',
          color: 'error',
          icon: <ErrorIcon sx={{ fontSize: 16 }} />
        };
      default:
        return {
          label: 'Unknown',
          color: 'default',
          icon: <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />
        };
    }
  };

  const statusDisplay = getConnectionStatusDisplay();

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        height: { xs: '100dvh', sm: '100vh' }, // Use dynamic viewport height on mobile
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden', // Prevent body scroll
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        zIndex: 1300, // Ensure it's above other content
      }}
    >
      {loading && (
        <Box sx={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          zIndex: 1000, // Higher z-index to be above Daily.co UI
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          borderRadius: 2,
          p: 4,
          minWidth: { xs: '280px', sm: '320px' },
        }}>
          <CircularProgress sx={{ color: 'white', mb: 2 }} />
          <Typography sx={{ mt: 2, mb: 3, color: 'white', fontWeight: 500 }}>
            Connecting to video call...
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={handleCancelCall}
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Cancel Call
          </Button>
        </Box>
      )}
      
      <Box 
        ref={containerRef} 
        sx={{ 
          flex: 1,
          backgroundColor: '#000',
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 0, // Important for flex children
          overflow: 'hidden',
        }}
      />

      {/* Connection Status Indicator - Only show when connected, positioned to not interfere with Daily.co UI */}
      {!loading && connectionStatus === 'connected' && (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 }, // Position on right to avoid Daily.co controls
            zIndex: 5, // Lower z-index than Daily.co UI elements
            pointerEvents: 'none', // Don't interfere with clicks
          }}
        >
          <Chip
            icon={statusDisplay.icon}
            label={statusDisplay.label}
            color={statusDisplay.color}
            size="small"
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              backdropFilter: 'blur(10px)',
              fontWeight: 500,
              pointerEvents: 'none', // Don't interfere with clicks
            }}
          />
        </Box>
      )}

      {/* Call Controls Overlay */}
      {!loading && callFrameRef.current && (
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 16, sm: 24 },
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            width: '100%',
            maxWidth: { xs: '100%', sm: '600px' },
            px: { xs: 2, sm: 0 },
          }}
        >
          <CallControls
            isMuted={isMuted}
            isVideoEnabled={isVideoEnabled}
            callType={callType}
            onToggleMute={handleToggleMute}
            onToggleVideo={handleToggleVideo}
            onHangUp={handleHangUp}
            disabled={loading || connectionStatus === 'error'}
          />
        </Box>
      )}
    </Box>
  );
};

export default VideoCall;
